import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";

import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { beneficiaryIdState } from "../../../../states/beneficiaryIdState";

import BeneficiaryCreditHistory from "./beneficiaryCreditHistory";

const BeneficiaryCredits = () => {
  const beneficiaryId = useAtom(beneficiaryIdState);

  const [isLoading, setIsLoading] = useAtom(loadingState);

  const [beneficiaryCreditHistoryVisible, setBeneficiaryCreditHistoryVisible] =
    useState(false);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("credits");

  const [entities, setEntities] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState(null);
  const [order, setOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "code",
          type: "string",
        },
        {
          name: "openedDate",
          type: "date",
        },
        {
          name: "application.amount",
          type: "decimal",
        },
        {
          name: "application.installmentsAmount",
          type: "decimal",
        },
        {
          name: "application.installmentsNumber",
          type: "number",
        },
        {
          name: "application.annualRate",
          type: "decimal",
        },
        {
          name: "pplication.annualDefaultRate",
          type: "decimal",
        },
        {
          name: "application.administrationFeeRate",
          type: "decimal",
        },
      ]);

      const response = await api.get(
        `${env("API_URL")}/credits/beneficiary/${beneficiaryId}/paged`,
        {
          params: {
            columns,
            page,
            pageSize,
            sort: sort ?? "code",
            order: order >= 0 ? "ASC" : "DESC",
            beneficiaryId,
            debouncedSearchTerm,
          },
        }
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      console.log(error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sort, order, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, sort, order, debouncedSearchTerm]);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex justify-content-center">
        <Button
          icon="pi pi-list"
          className="p-button-rounded p-button-primary mr-2"
          onClick={() => showBeneficiaryCreditHistoryVisible(rowData?.id)}
        />
      </div>
    );
  };

  const showBeneficiaryCreditHistoryVisible = (creditId) => {
    setBeneficiaryCreditHistoryVisible(true);
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-direction-columnflex flex-column">
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setPage(e.first);
            setPageSize(e.rows);
          }}
          onSort={(e) => {
            setSort(e.sortField);
            setOrder(e.sortOrder);
          }}
          sortField={sort}
          sortOrder={order}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={page}
          rows={pageSize}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Credits")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
        >
          <Column
            field="code"
            header={t("entities.credits.code")}
            bodyStyle={{ textAlign: "center" }}
            sortable
          ></Column>
          <Column
            field="openedDate"
            header={t("entities.credits.openedDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.isDate(rowData.openedDate)
                ? helperFormats.formatDate(new Date(rowData.openedDate))
                : "Pendiente"
            }
            sortable
          ></Column>
          <Column
            field="application.amount"
            header={t("entities.credits.application.amount")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.application.amount);
            }}
            sortable
          ></Column>
          <Column
            field="application.installmentsAmount"
            header={t("entities.credits.application.installmentsAmount")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(
                rowData.application.installmentsAmount
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.installmentsNumber"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.credits.application.installmentsNumber")}
            sortable
          ></Column>
          <Column
            field="application.annualRate"
            header={t("entities.credits.application.annualRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(rowData.application.annualRate)
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.annualDefaultRate"
            header={t("entities.credits.application.annualDefaultRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(rowData.application.annualDefaultRate)
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.administrationFeeRate"
            header={t("entities.credits.application.administrationFeeRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(rowData.application.administrationFeeRate)
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "8rem", textAlign: "center" }}
          ></Column>
        </DataTable>
      </div>
      <div>
        {beneficiaryCreditHistoryVisible && <BeneficiaryCreditHistory />}
      </div>
    </div>
  );
};

export default BeneficiaryCredits;
